/* eslint-disable react/display-name */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-03-17 14:47:01
 */
import React, { useEffect, useState } from 'react'
import {
  Button,
  Empty,
  Layout,
  Table,
  message,
  Modal,
  Popover,
  Radio
} from 'antd'
import {
  useAppDispatch,
  useAppSelector,
  useIsAdmin,
  usePayment,
  useVip
} from '../../../app/hooks'
import {
  deleteCustomers,
  downloadCustomers,
  fetchCustomerList
} from '../../../actions/customer'
import Edit from './Edit'
import sensors from 'sa-sdk-javascript'
import { sensorsRole } from '../../../utils/sensors'
import BatchImport from './BatchImport'
import Filter from './Filter'
import vip_download_excel_icon from '../../../images/vip_download_excel.png'
import { setPaymentVisible } from '../../../actions/main'
import DownloadHistory from './DownloadHistory'
import { fetchCustomersRules } from '../../../actions/group'
import { createDepartmentsColumns } from './lib'
import Viewer from '../../components/ViewerV2'
import type { ViewerRef } from '../../components/ViewerV2/types'
import PhotoWithTooltip from '../../rectify/components/rectifyTable/components/PhotoWithTooltip'
import usePhotoViewer from '../../rectify/components/rectifyTable/hooks/usePhotoViewer'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

export default () => {
  const groupId = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const customerConfig = useAppSelector((state) => state.group.customerConfig)
  const dispatch = useAppDispatch()
  const { isSVip } = useVip()

  const {
    viewerRef,
    viewerPhotos,
    activeTooltip,
    setActiveTooltip,
    handlePhotoPreview
  } = usePhotoViewer(dispatch)

  const baseColumnsConfig = [
    {
      title: '序号',
      width: 80,
      dataIndex: 'index'
    },
    {
      title: `${customerConfig.noun}ID`,
      dataIndex: 'customID',
      key: 'customID',
      width: 100
    },
    {
      title: `${customerConfig.noun}名称`,
      width: 200,
      dataIndex: 'customerName'
    },
    {
      title: `${customerConfig.noun}地址`,
      width: 200,
      dataIndex: 'address'
    },
    {
      title: '门店照片',
      width: 200,
      dataIndex: 'coverFiles',
      key: 'coverFiles',
      render: (photos, record) => (
        <PhotoWithTooltip
          photos={photos}
          type="门店照片"
          record={record}
          activeTooltip={activeTooltip}
          setActiveTooltip={setActiveTooltip}
          handlePhotoPreview={handlePhotoPreview}
        />
      )
    },
    {
      title: '联系人',
      width: 160,
      dataIndex: 'contractName'
    },
    {
      title: '联系电话',
      width: 160,
      dataIndex: 'contractTelPhone'
    },
    {
      title: '创建时间',
      width: 200,
      dataIndex: 'createdTime'
    },
    {
      title: '跟进人及电话',
      width: 200,
      dataIndex: 'owner',
      render: (owner) => (
        <>
          {owner.map(({ mobile, name }, index) => (
            <div key={index}>{`${name} - ${mobile}`}</div>
          ))}
        </>
      )
    },
    {
      title: `上次${customerConfig.verb}`,
      width: 200,
      dataIndex: 'visitTime'
    },
    {
      title: '详情',
      width: 160,
      fixed: 'right',
      key: 'detail',
      render: () => {
        return (
          <a
            onClick={() => {
              reportSaClickEvent('edit')
              setEditVisible(true)
            }}
          >
            编辑
          </a>
        )
      }
    }
  ]

  const defaultFilter = {
    members: [],
    departmentUsers: [],
    departments: [],
    keyword: ''
  }

  const defaultPagintion = {
    current: 1,
    total: 0,
    pageSize: 20
  }

  const [filter, setFilter] = useState<{
    members: any[]
    departmentUsers: { userID: string }[]
    departments: { id: string; num: number }[]
    keyword: string
  }>(defaultFilter)
  const [data, setData] = useState<any[]>([])
  const [editVisible, setEditVisible] = useState(false)
  const [batchImportVisible, setBatchImportVisible] = useState(false)
  const [columns, setColumns] = useState<any[]>(baseColumnsConfig)
  const [downloadHistoryVisible, setDownloadHistoryVisible] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [downloadConfirmVisible, setDownloadConfirmVisible] = useState(false)
  const [includeStorePhoto, setIncludeStorePhoto] = useState(true)
  const payment = usePayment()

  const [pagination, setPagination] = useState({
    ...defaultPagintion,
    showTotal: (total) => `共 ${total} 条`,
    showSizeChanger: false
  })

  const isAdmin = useIsAdmin()

  const reportSaClickEvent = (clickItem) => {
    sensors.track('web_workgroup_customer_info_click', {
      clickItem,
      role: sensorsRole(myRole),
      groupID: groupId,
      content: filter.keyword
    })
  }

  const handleFetchCustomerList = (page, filter) => {
    const departmentIDList = (filter.departments || []).map((department) =>
      Number(department.id)
    )
    const userIDList = (filter.departmentUsers || []).map((user) => user.userID)
    dispatch(
      fetchCustomerList({
        groupID: groupId,
        pageNo: page,
        pageSize: pagination.pageSize,
        keyword: filter.keyword,
        userIDList,
        departmentIDList
      })
    )
      .then((res) => {
        setData(
          (res.customers || []).map((item, index) => ({
            ...item,
            index: index + 1 + (page - 1) * pagination.pageSize
          }))
        )
        setPagination({
          ...pagination,
          current: page,
          total: res.totalCustomer
        })

        const labelColumns = (res.labelGroups || []).map((item) => {
          return {
            title: item.name,
            width: 110,
            key: item.ID,
            render: (_, record) => {
              return (record.labelInfo || {})[item.ID] || ''
            }
          }
        })

        const departmentsColumns = createDepartmentsColumns(res.customers || [])

        const newColumns: any[] = [...baseColumnsConfig]

        const createdTimeColumnIndex = newColumns.findIndex(
          (item) => item.dataIndex === 'createdTime'
        )
        newColumns.splice(createdTimeColumnIndex, 0, ...labelColumns) //客户标签放在创建时间前面

        const ownerColumnIndex = newColumns.findIndex(
          (item) => item.dataIndex === 'owner'
        )
        newColumns.splice(ownerColumnIndex + 1, 0, ...departmentsColumns) //部门放在跟进人后面

        setColumns(newColumns)
      })
      .catch(() => {
        setData([])
        setPagination({
          ...pagination,
          ...defaultPagintion
        })
        setColumns([])
      })
  }

  const handleSubmit = () => {
    reportSaClickEvent('beginSearch')
    handleFetchCustomerList(1, filter)
  }

  const handleReset = () => {
    setFilter(defaultFilter)
    handleFetchCustomerList(1, defaultFilter)
  }

  useEffect(() => {
    handleFetchCustomerList(1, defaultFilter)
  }, [])

  const handleDownload = (includeStorePhoto = false) => {
    reportSaClickEvent('exportCustomer')

    dispatch(
      fetchCustomersRules({
        groupID: groupId
      })
    ).then(
      ({ visitAggTypePurchaseCount, visitAggTypeUseCount, customerCount }) => {
        if (customerCount > 100 && visitAggTypePurchaseCount === 0) {
          return message.info(
            `请前往app购买「${customerConfig.noun}${customerConfig.verb}权益」`
          )
        } else if (
          // 195版本客户拜访可购买30人版，取消无限人数版本。 旧的无限人数版已购名额会返回int32最大值
          customerCount > 100 &&
          // visitAggTypePurchaseCount < 30 &&
          visitAggTypePurchaseCount < visitAggTypeUseCount
        ) {
          return message.info(
            `${customerConfig.noun}${customerConfig.verb}使用人数超出，请前往app进行调整`
          )
        }
        const departmentIDList = (filter.departments || []).map((department) =>
          Number(department.id)
        )

        const userIDList = (filter.departmentUsers || []).map(
          (user) => user.userID
        )

        const customerIDs = (selectedRowKeys || []).map((id) => Number(id))

        const downloadParams = includeStorePhoto
          ? {
              groupId,
              userIDList,
              departmentIDList,
              customerIDs,
              includeStorePhoto: true
            }
          : { groupId, userIDList, departmentIDList, customerIDs }

        dispatch(downloadCustomers(downloadParams)).then(() => {
          setDownloadHistoryVisible(true)
        })
      }
    )

    // if (!vip.isSVip) {
    //   payment.openSvip('webExportCostomer')
    // } else {
    //   dispatch(
    //     downloadCustomers({
    //       groupId
    //     })
    //   ).then(() => {
    //     setDownloadHistoryVisible(true)
    //   })
    // }
  }

  const handleBatchDelete = () => {
    reportSaClickEvent('batchDelete')
    if (selectedRowKeys.length === 0) {
      return Modal.info({
        title: '请选择要删除的' + customerConfig.noun
      })
    }
    if (!isSVip) {
      payment.openSvip('webBatchDeleteCustomer')
      return
    }
    Modal.confirm({
      title: `将同时删除所选${customerConfig.noun}的${customerConfig.verb}记录，是否确认删除？`,
      okText: '删除',
      okButtonProps: {
        danger: true
      },
      onOk: () => {
        reportSaClickEvent('confirmBatchDelete')
        dispatch(
          deleteCustomers({
            groupId,
            customerIDs: selectedRowKeys
          })
        ).then(() => {
          setSelectedRowKeys([])
          handleFetchCustomerList(1, filter)
        })
      }
    })
  }

  const onCancelAll = () => {
    setSelectedRowKeys([])
  }

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      // 服务端分页：合并之前页面保留的 keys 和当前页操作后的 keys
      const currentPageIds = new Set(data.map((item: any) => item.id))
      // 保留不在当前页的旧选中
      const preservedKeys = selectedRowKeys.filter(
        (key) => !currentPageIds.has(key)
      )
      // 当前页新选中的
      const currentPageKeys = newSelectedRowKeys.filter((key) =>
        currentPageIds.has(key)
      )
      setSelectedRowKeys([...preservedKeys, ...currentPageKeys])
    }
  }

  const scrollWidth = columns.reduce((w, cur) => w + cur.width, 0)

  const handleOpenDownloadConfirm = () => {
    setIncludeStorePhoto(true)
    setDownloadConfirmVisible(true)
  }

  const handleConfirmDownload = () => {
    if (includeStorePhoto && pagination.total > 10000) {
      message.info(`${customerConfig.noun}数量过多，请分批导出。`)
      return
    }
    setDownloadConfirmVisible(false)
    handleDownload(includeStorePhoto)
  }

  return (
    <Layout className="Customer">
      <PageHeader
        className="Header"
        ghost={true}
        title={`${customerConfig.noun}信息`}
        extra={
          <>
            <Popover
              content="暂无权限，请联系管理员"
              trigger={isAdmin ? [] : 'hover'}
            >
              <Button
                type="primary"
                onClick={() => {
                  reportSaClickEvent('batch')
                  setBatchImportVisible(true)
                }}
                disabled={!isAdmin}
              >
                批量新增
              </Button>
            </Popover>

            <Popover
              content="暂无权限，请联系管理员"
              trigger={isAdmin ? [] : 'hover'}
            >
              <Button
                type="primary"
                ghost
                onClick={handleBatchDelete}
                disabled={!isAdmin}
              >
                批量删除
                <img
                  alt=""
                  src={vip_download_excel_icon}
                  style={{
                    width: 20,
                    height: 20,
                    verticalAlign: 'bottom'
                  }}
                />
              </Button>
            </Popover>

            <Button
              type="primary"
              ghost
              onClick={handleOpenDownloadConfirm}
              disabled={data.length === 0}
            >
              批量导出
              <img
                alt=""
                src={vip_download_excel_icon}
                style={{
                  width: 20,
                  height: 20,
                  verticalAlign: 'bottom'
                }}
              />
            </Button>

            <Button
              type="link"
              onClick={() => {
                setDownloadHistoryVisible(true)
              }}
            >
              下载记录
            </Button>
          </>
        }
      />
      <Content className="content">
        <Modal
          open={downloadConfirmVisible}
          title={`是否含${customerConfig.noun}照片`}
          okText="确认"
          cancelText="取消"
          onOk={handleConfirmDownload}
          onCancel={() => setDownloadConfirmVisible(false)}
        >
          <Radio.Group
            value={includeStorePhoto}
            onChange={(e) => setIncludeStorePhoto(e.target.value)}
          >
            <Radio value={true}>是</Radio>
            <Radio value={false}>否</Radio>
          </Radio.Group>
        </Modal>
        <Filter
          filter={filter}
          setFilter={setFilter}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onReportSaClickEvent={reportSaClickEvent}
        />
        {data.length ? (
          <div className="table-wrapper">
            {selectedRowKeys.length > 0 && (
              <div className="selected-row">
                已选{selectedRowKeys.length}个
                <Button type="link" onClick={onCancelAll}>
                  取消选择
                </Button>
              </div>
            )}
            <Table
              bordered
              rowSelection={rowSelection}
              dataSource={data}
              columns={columns}
              pagination={{ ...pagination }}
              onChange={(pagination) => {
                handleFetchCustomerList(pagination.current, filter)
              }}
              rowKey="id"
              scroll={{
                x: scrollWidth,
                y: window.innerHeight - 380,
                scrollToFirstRowOnChange: true
              }}
            />
          </div>
        ) : (
          <Empty style={{ marginTop: '300px' }} />
        )}
      </Content>

      <Edit visible={editVisible} onClose={() => setEditVisible(false)} />
      <BatchImport
        visible={batchImportVisible}
        onClose={() => setBatchImportVisible(false)}
        onFinish={() => {
          setBatchImportVisible(false)
          handleFetchCustomerList(1, filter)
        }}
      />

      <DownloadHistory
        visible={downloadHistoryVisible}
        onClose={() => setDownloadHistoryVisible(false)}
      />

      <Viewer
        ref={viewerRef as React.RefObject<ViewerRef>}
        photos={viewerPhotos}
        showWatermarkAction={false}
        isShowComments={false}
        fromPlace="customerStorePhotos"
        canDelete={false}
        canRectify={false}
        canAiSecurity={false}
      />
    </Layout>
  )
}
